'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { updateCustomer } from '@eliadmin/core/src/api/customers/customers-api';
import type { Customer } from '@eliadmin/core/src/api/customers/customers-types';
import { ACCOUNT_COPY } from '@eliadmin/core/src/components/dlz/account/account-copy';
import {
  BillingCard,
  DeliveryCard,
} from '@eliadmin/core/src/components/dlz/account/profile/address-cards';
import { CommunicationCard } from '@eliadmin/core/src/components/dlz/account/profile/communication-card';
import { CompanyCard } from '@eliadmin/core/src/components/dlz/account/profile/company-card';
import { DeleteAccount } from '@eliadmin/core/src/components/dlz/account/profile/delete-account';
import {
  FormResultMessage,
  type FormResult,
} from '@eliadmin/core/src/components/dlz/account/profile/form-result';
import { PersonalCard } from '@eliadmin/core/src/components/dlz/account/profile/personal-card';
import { PROFILE_COPY } from '@eliadmin/core/src/components/dlz/account/profile/profile-copy';
import {
  profileFormSchema,
  type ProfileFormInput,
  type ProfileFormValues,
} from '@eliadmin/core/src/components/dlz/account/profile/profile-schema';
import {
  SERVER_FIELD_MAP,
  toCustomerUpdate,
  toProfileFormValues,
} from '@eliadmin/core/src/components/dlz/account/profile/profile-values';
import { applyServerErrors } from '@eliadmin/core/src/components/dlz/account/profile/server-errors';
import { useCountries } from '@eliadmin/core/src/components/dlz/account/profile/use-countries';
import { defaultCountryId } from '@eliadmin/core/src/components/dlz/auth/register/register-values';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Form } from '@eliadmin/core/src/components/ui/form';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

/**
 * The cards of „Môj profil“ (Figma „Content“ 60321:19137) as one form. „Uložiť zmeny“ sends the
 * customer fields with `updateCustomer` and puts the answer into the customer store like the core
 * profile form; a 422 goes onto the fields it names. The newsletter switch acts on its own.
 *
 * The result sits in the place of the Figma „Spacer“ (60321:19324): beside the button on wide
 * screens, between the button and „Zrušiť konto“ on phones, where its 1 px minimum keeps the designed
 * 16 + 1 + 16 px while it is empty. A success disappears with the next edit.
 */
export function ProfileForm({ customer, authToken }: { customer: Customer; authToken: string }) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const { formValidationMessages } = useFormValidationMessages(t);
  const countries = useCountries();
  const defaultCountry = defaultCountryId(countries);
  const isCompany = !!customer.company_buy;
  const [result, setResult] = useState<FormResult>(null);

  const form = useForm<ProfileFormInput, unknown, ProfileFormValues>({
    resolver: zodResolver(profileFormSchema(formValidationMessages, isCompany)),
    defaultValues: toProfileFormValues(customer, defaultCountry),
  });
  const sameDelivery = useWatch({ control: form.control, name: 'same_delivery' });
  const { isDirty } = form.formState;

  const onSubmit = form.handleSubmit(async values => {
    setResult(null);
    try {
      const { data } = await updateCustomer({
        locale,
        authToken,
        data: toCustomerUpdate(values, countries, isCompany),
      });
      const updated = { ...customer, ...data };
      initCustomerStore({ authToken, customer: updated });
      form.reset(toProfileFormValues(updated, defaultCountry));
      setResult({ status: 'success' });
    } catch (error) {
      const rest = applyServerErrors(error, form.setError, field => SERVER_FIELD_MAP[field]);
      if (rest)
        setResult({ status: 'failed', message: rest.join('\n') || PROFILE_COPY.saveFailed });
    }
  });

  return (
    <Form {...form}>
      <form
        noValidate
        aria-label={ACCOUNT_COPY.pages.profile.label}
        onSubmit={onSubmit}
        className="flex flex-col gap-6"
      >
        <div className="flex flex-col gap-4 md:gap-6">
          <PersonalCard company={isCompany} />
          {isCompany ? <CompanyCard /> : null}
          <BillingCard countries={countries} />
          {sameDelivery ? null : <DeliveryCard countries={countries} />}
          <CommunicationCard customer={customer} authToken={authToken} />
        </div>
        <div className="flex flex-col items-center gap-4 md:flex-row">
          <Button
            type="submit"
            variant="success"
            isLoading={form.formState.isSubmitting}
            className="h-12 w-full md:h-[52px] md:w-auto"
          >
            {t('text2290')}
          </Button>
          <FormResultMessage
            result={result?.status === 'success' && isDirty ? null : result}
            success={t('text3119')}
            className="flex min-h-px w-full justify-center text-center md:w-auto md:min-w-0 md:flex-1 md:justify-start md:text-left"
          />
          <DeleteAccount authToken={authToken} />
        </div>
      </form>
    </Form>
  );
}
